import React from "react";
import { ActivityIndicator, FlatList, Pressable, StyleSheet, TouchableOpacity, View, ViewProps } from "react-native";
import { useSelector } from "react-redux";

import { tReferralResponse, tReferralStatus } from "tmt_api/src/types/types.validator";
import * as mappers from "tmt_api/src/shared/mappers";

import * as utilsDb from "src/db/utils.db";
import * as referralDb from "src/db/referral.db";
import { PdFormLabel } from "src/components/form-control";
import { PdBadge, PdBadgeLabel, PdText } from "src/components/layout";
import {
  PdTableCell,
  PdTableCellText,
  PdTableEmpty,
  PdTablePaginationFooter,
  PdTableRow,
  PdTableSortableHeaders,
} from "src/components/layout/table";
import { tColumn, tRowInstance, useTable } from "src/hooks/table";
import { useIsMobile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { formatDate, formatMoney, formatMoneyShort } from "src/util/gFormatting";

import { useIsFocused } from "@react-navigation/native";
import { useReferCompaniesModal } from "src/hooks/modals";
import { kTablePageSize } from "src/util/constants";
import { tPressableState } from "src/components/types";
import { dReferralStatus } from "tmt_api/src/shared/dics";
import { useAppDispatch } from "src/redux";
import { changeFilterNextPage, changeFilterPrevPage, resetFilters } from "src/redux/filters/actions";

type tTableState = {
  data: tReferralResponse[];
  isLoading: boolean;
  prev: string | null;
  next: string | null;
};

const getThemeColorFromReferralStatus = (status: tReferralStatus): string => {
  if (status === "invited") return kColors.pink;
  if (status === "waitlisted") return kColors.orange200;
  if (status === "joined") return kColors.green200;
  return kColors.gray300;
};

const getReferralLabel = (referral: tReferralResponse) => {
  return dReferralStatus[referral.status] ?? "";
};

const PdCard: React.FC<ViewProps & { variant?: "outline" | "solid" }> = ({ variant = "outline", style, ...rest }) => {
  const isMobile = useIsMobile();

  const dynamicStyles = variant === "solid" ? styles.bgGreen : [styles.border1, styles.borderGray];

  return (
    <View style={[dynamicStyles, isMobile ? [styles.px4, styles.py3] : [styles.px7, styles.py3], style]} {...rest} />
  );
};

const Row: React.FC<{
  item: tRowInstance<tReferralResponse>;
  onPress?: (item: tRowInstance<tReferralResponse>) => void;
}> = ({ item, onPress }) => {
  const handleOnPress = () => {
    onPress?.(item);
  };

  return (
    <Pressable disabled={!item.canSendEmail} onPress={handleOnPress}>
      {({ pressed, hovered }: tPressableState) => (
        <PdTableRow isPressed={pressed} isHovered={hovered}>
          {item.cells.map((cell) => (
            <PdTableCell key={cell.uniqueId} style={cell.style}>
              {cell.render("Cell")}
            </PdTableCell>
          ))}
        </PdTableRow>
      )}
    </Pressable>
  );
};

const BrandInviteMobileRow: React.FC<{
  item: tRowInstance<tReferralResponse>;
  onPress?: (item: tRowInstance<tReferralResponse>) => void;
}> = ({ item, onPress }) => {
  const handleOnPress = () => {
    onPress?.(item);
  };

  return (
    <TouchableOpacity disabled={!item.canSendEmail} onPress={handleOnPress} style={[styles.flexRow, styles.py3]}>
      <View style={styles.flex1}>
        <PdText style={styles.mb1}>{item.receiverName ?? item.receiverEmail ?? "-"}</PdText>
        <PdBadge style={[styles.mb1, styles.mrAuto, { backgroundColor: getThemeColorFromReferralStatus(item.status) }]}>
          <PdBadgeLabel>{getReferralLabel(item)}</PdBadgeLabel>
        </PdBadge>
        <View style={[styles.flexRow, styles.alignCenter]}>
          <PdFormLabel style={styles.mr1}>Total Earned</PdFormLabel>
          <PdText>-</PdText>
        </View>
      </View>
      <View style={[styles.flex1, styles.justifyBetween, styles.alignEnd]}>
        <PdText>{formatDate(item.createdAt.seconds * 1000)}</PdText>
        {item.canSendEmail && (
          <PdBadge style={[styles.bgRed, styles.mb1]}>
            <PdBadgeLabel>{item.timesEmailSent === 1 ? "Send 2nd Invite" : "Send 3rd Invite"}</PdBadgeLabel>
          </PdBadge>
        )}
      </View>
    </TouchableOpacity>
  );
};

const SalesRepInviteMobileRow: React.FC<{
  item: tRowInstance<tReferralResponse>;
  onPress?: (item: tRowInstance<tReferralResponse>) => void;
}> = ({ item, onPress }) => {
  const handleOnPress = () => {
    onPress?.(item);
  };

  return (
    <TouchableOpacity disabled={!item.canSendEmail} onPress={handleOnPress} style={styles.py3}>
      <PdText style={styles.mb1}>{item.receiverName ?? item.receiverEmail ?? "-"}</PdText>
      <PdBadge style={[styles.mb1, styles.mrAuto, { backgroundColor: getThemeColorFromReferralStatus(item.status) }]}>
        <PdBadgeLabel>{item.status}</PdBadgeLabel>
      </PdBadge>
      <View style={[styles.flexRow, styles.alignCenter]}>
        <PdFormLabel style={styles.mr1}>Total Earned</PdFormLabel>
        <PdText>-</PdText>
      </View>
    </TouchableOpacity>
  );
};

export const MyInvitesCard: React.FC<ViewProps & { type: "brand" | "rep" }> = ({ type, ...rest }) => {
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const isFocused = useIsFocused();
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const referCompaniesModal = useReferCompaniesModal();
  const filters = useSelector((state) => state.FiltersReducer.referrals);
  const [tableState, setTableState] = React.useState<tTableState>({
    isLoading: true,
    data: [],
    prev: null,
    next: null,
  });
  const tableInstance = useTable<tReferralResponse>({
    data: tableState.data,
    columns: type === "brand" ? myBrandsInvitesColumns : mySalesRepsInvitesColumns,
    initialState: {
      sortedBy: "createdAt",
      sortDirection: "desc",
    },
  });

  const handleOnPressPrevPage = () => {
    dispatch(changeFilterPrevPage({ pageName: "referrals", value: tableState?.prev ?? "" }));
  };

  const handleOnPressNextPage = () => {
    dispatch(changeFilterNextPage({ pageName: "referrals", value: tableState?.next ?? "" }));
  };

  const handleOnPressRow = React.useCallback(
    (item: tRowInstance<tReferralResponse>) => {
      referCompaniesModal.openAsync({
        referralId: item.id,
        type: item.type,
        defaultEmailsList: item.receiverEmail,
      });
    },
    [referCompaniesModal],
  );

  const renderItem: React.FC<{ item: tRowInstance<tReferralResponse> }> = React.useCallback(
    ({ item }) => {
      if (isMobile) {
        return type === "brand" ? (
          <BrandInviteMobileRow item={item} onPress={handleOnPressRow} />
        ) : (
          <SalesRepInviteMobileRow item={item} onPress={handleOnPressRow} />
        );
      }
      return <Row item={item} onPress={handleOnPressRow} />;
    },
    [isMobile, type, handleOnPressRow],
  );

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const query = await utilsDb.withPagination(referralDb.queryReferral, referralDb.documentReferral, {
        senderId: rCompanyId,
        type,
        prev: filters.prev,
        next: filters.next,
        limit: kTablePageSize,
      });

      unsubscribe = query.onSnapshot((snapshot) => {
        const data = snapshot.docs.map((doc) => mappers.mapReferral(doc.data()));
        setTableState({
          data,
          isLoading: false,
          prev: data[0]?.id ?? "",
          next: data.length >= kTablePageSize ? data[data.length - 1]?.id ?? "" : "",
        });
      });
    })();

    return () => {
      unsubscribe?.();
    };
  }, [filters.next, filters.prev, rCompanyId, type]);

  React.useEffect(() => {
    if (!isFocused) {
      dispatch(resetFilters());
    }
  }, [dispatch, isFocused]);

  return (
    <PdCard {...rest}>
      <PdText style={[styles.fontMedium, styles.textLg, styles.mb4]}>
        {type === "brand" ? "My Brand Invites" : "My Sales Reps Invites"}
      </PdText>
      {tableState.isLoading ? (
        <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
          <ActivityIndicator size="large" color={kColors.brandGreen} />
        </View>
      ) : (
        <FlatList
          ListHeaderComponent={!isMobile ? <PdTableSortableHeaders headers={tableInstance.headers} /> : null}
          ListEmptyComponent={
            <PdTableEmpty
              message={
                type === "brand"
                  ? "You haven't invited any brands yet. Invite your brands and earn cash rewards!"
                  : "You haven't invited any reps yet. Invite your reps and earn cash rewards!"
              }
            />
          }
          data={tableInstance.rows}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          key={`${isMobile}`}
        />
      )}
      <PdTablePaginationFooter
        style={[styles.alignCenter, styles.mlAuto]}
        page={filters.page}
        hasNext={!!tableState?.next}
        onPressBack={handleOnPressPrevPage}
        onPressNext={handleOnPressNextPage}
      />
    </PdCard>
  );
};

export const ReferBrandsCard: React.FC<ViewProps> = ({ style }) => {
  const [stats, setStats] = React.useState<{ totalInvited: number; potentialRewards: number; totalEarned: number }>({
    totalInvited: 0,
    potentialRewards: 0,
    totalEarned: 0,
  });

  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const rAvailableCredit = useSelector((state) =>
    state.CompanyReducer.type === "retailer" ? state.CompanyReducer.availableRewardsCreditAmount ?? 0 : 0,
  );

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const totalInvitedQuery = await referralDb.queryReferral({
        senderId: rCompanyId,
        status: "invited",
        type: "brand",
      });
      unsubscribe = totalInvitedQuery.onSnapshot((snapshot) => {
        setStats((prev) => ({
          ...prev,
          totalInvited: snapshot.size,
          potentialRewards: snapshot.size * 2000,
        }));
      });
    })();

    return () => {
      unsubscribe?.();
    };
  }, [rCompanyId]);

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const totalEarnedQuery = await referralDb.queryReferral({ senderId: rCompanyId, type: "brand" });
      unsubscribe = totalEarnedQuery.onSnapshot((snapshot) => {
        setStats((prev) => ({
          ...prev,
          totalEarned: snapshot.docs.reduce(
            (total, doc) => total + doc.data().senderMarketplaceCreditAmount + doc.data().senderOrdersCreditAmount,
            0,
          ),
        }));
      });
    })();

    return () => {
      unsubscribe?.();
    };
  }, [rCompanyId]);

  return (
    <PdCard variant="solid" style={[style, styles.justifyBetween]}>
      <View>
        <PdFormLabel>Brands Invited</PdFormLabel>
        <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite]}>{stats.totalInvited}</PdText>
      </View>
      <View>
        <PdFormLabel>Potential Rewards</PdFormLabel>
        <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite]}>
          {formatMoneyShort(stats.potentialRewards)}
        </PdText>
      </View>
      <View>
        <PdFormLabel>Available Credit</PdFormLabel>
        <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite]}>{formatMoney(rAvailableCredit)}</PdText>
      </View>
      <View>
        <PdFormLabel>Total Earned</PdFormLabel>
        <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite]}>{formatMoney(stats.totalEarned)}</PdText>
      </View>
    </PdCard>
  );
};

export const ReferRepsCard: React.FC<ViewProps> = ({ style }) => {
  return (
    <PdCard variant="solid" style={[style, styles.justifyBetween]}>
      <View>
        <PdFormLabel>Sales Reps Invited</PdFormLabel>
        <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite]}>{0}</PdText>
      </View>
      <View>
        <PdFormLabel>Potential Rewards</PdFormLabel>
        <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite]}>{formatMoneyShort(0)}</PdText>
      </View>
      <View>
        <PdFormLabel>Credit Earned</PdFormLabel>
        <PdText style={[styles.fontMedium, styles.textLg, styles.textWhite]}>{formatMoney(0)}</PdText>
      </View>
    </PdCard>
  );
};

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  alignEnd: {
    alignItems: "flex-end",
  },
  bgGreen: {
    backgroundColor: kColors.brandGreen,
  },
  bgRed: {
    backgroundColor: kColors.red200,
  },
  border1: {
    borderWidth: 1,
  },
  borderGray: {
    borderColor: kColors.gray300,
  },
  flex1: {
    flex: 1,
  },
  flex2: {
    flex: 2,
  },
  flexRow: {
    flexDirection: "row",
  },
  fontMedium: {
    fontWeight: "400",
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  justifyCenter: {
    justifyContent: "center",
  },
  mb1: {
    marginBottom: kStyleConst.kSpacing * 1,
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mr1: {
    marginRight: kStyleConst.kSpacing * 1,
  },
  mrAuto: {
    marginRight: "auto",
  },
  px4: {
    paddingHorizontal: kStyleConst.kSpacing * 4,
  },
  px7: {
    paddingHorizontal: kStyleConst.kSpacing * 7,
  },
  py3: {
    paddingVertical: kStyleConst.kSpacing * 3,
  },
  textLg: {
    fontSize: 22,
  },
  textWhite: {
    color: kColors.white,
  },
});

const myBrandsInvitesColumns: tColumn<tReferralResponse>[] = [
  {
    id: "createdAt",
    header: "Date",
    accessor: (originalRow) => formatDate(originalRow.createdAt.seconds * 1000),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "brandName",
    header: "Brand Name",
    accessor: (originalRow) => originalRow.receiverName ?? originalRow.receiverEmail ?? "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    isSortable: false,
    containerStyle: styles.flex2,
    style: styles.flex2,
  },
  {
    id: "status",
    header: "Status",
    accessor: (originalRow) => originalRow.status,
    Cell: ({ row }) => (
      <PdBadge style={{ backgroundColor: getThemeColorFromReferralStatus(row.status) }}>
        <PdBadgeLabel>{getReferralLabel(row)}</PdBadgeLabel>
      </PdBadge>
    ),
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "marketplaceCreditAmount",
    header: "Main Tab Credit",
    accessor: (originalRow) => formatMoney(originalRow.senderMarketplaceCreditAmount),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "otherCreditAmount",
    header: "Other Orders Credit",
    accessor: (originalRow) => formatMoney(originalRow.senderOrdersCreditAmount),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "totalEarned",
    header: "Total Earned",
    accessor: (originalRow) =>
      formatMoney(originalRow.senderMarketplaceCreditAmount + originalRow.senderOrdersCreditAmount),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "invite-again",
    header: "Invite Again",
    accessor: (originalRow) => originalRow.id,
    Cell: ({ row }) =>
      row.canSendEmail && (
        <PdBadge style={styles.bgRed}>
          <PdBadgeLabel>{row.timesEmailSent === 1 ? "Send 2nd Invite" : "Send 3rd Invite"}</PdBadgeLabel>
        </PdBadge>
      ),
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
];

const mySalesRepsInvitesColumns: tColumn<tReferralResponse>[] = [
  {
    id: "salesRepName",
    header: "Sales Rep Name",
    accessor: (originalRow) => originalRow.receiverName ?? originalRow.receiverEmail ?? "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "status",
    header: "Status",
    accessor: (originalRow) => originalRow.status,
    Cell: ({ row }) => (
      <PdBadge style={{ backgroundColor: getThemeColorFromReferralStatus(row.status) }}>
        <PdBadgeLabel>{getReferralLabel(row)}</PdBadgeLabel>
      </PdBadge>
    ),
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
  {
    id: "totalAmount",
    header: "Total Earned",
    accessor: () => "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    isSortable: false,
    containerStyle: styles.flex1,
    style: styles.flex1,
  },
];
